import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs';
import { MESSAGE_CONSTANTS, NIGHTWEB_APP_CONSTANT } from 'src/app/constants';
import { PasswordValidation } from 'src/app/directives';
import { AuthService, ToasterService, UserService } from 'src/app/services';

@Component({
  selector: 'app-end-user-activate',
  templateUrl: './end-user-activate.component.html',
  styleUrls: ['./end-user-activate.component.scss'],
  standalone: false
})
export class EndUserActivateComponent implements OnInit {

  activateForm!: FormGroup;

  loading = false;
  submitted = false;
  showNewPassword = false;
  showConfirmPassword = false;
  date = new Date();
  verifyToken: string = '';
  email: string = '';
  cardTitle: string = 'Create your account';
  subTitle: string = 'Set a password to finish activating your NightWeb account.';
  isForgotPassword: boolean = false;
  buttonText: string = 'Create Account';

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private toasterService: ToasterService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['token']) {
        this.verifyToken = params['token'];
      }
      if (params['email']) {
        this.email = decodeURIComponent(params['email']);
      }
      if (params['type']) {
        this.isForgotPassword = true;
        this.cardTitle = 'Reset your password';
        this.subTitle = 'Enter a new password to reset your NightWeb account.';
        this.buttonText = 'Reset Password';
      }
      this.setActivateForm();
    });
  }

  get passwordValue(): string {
    return this.activateForm?.get('newPassword')?.value || '';
  }

  get hasMinLength(): boolean {
    return this.passwordValue.length >= NIGHTWEB_APP_CONSTANT.passwordMinLength;
  }

  get hasUppercase(): boolean {
    return /[A-Z]/.test(this.passwordValue);
  }

  get hasSpecialAndNumber(): boolean {
    return /[#?!@$%^&*-]/.test(this.passwordValue) && /[0-9]/.test(this.passwordValue);
  }

  get passwordsMatch(): boolean {
    const confirm = this.activateForm?.get('confirmNewPassword')?.value || '';
    return !!this.passwordValue && this.passwordValue === confirm;
  }

  ruleIcon(passed: boolean): 'exclamation-circle' | 'check-circle' | 'x-circle' {
    if (!this.passwordValue) {
      return 'exclamation-circle';
    }
    return passed ? 'check-circle' : 'x-circle';
  }

  ruleClass(passed: boolean): string {
    if (!this.passwordValue) {
      return 'text-gray-dark';
    }
    return passed ? 'text-success' : 'text-danger';
  }

  setActivateForm() {
    this.activateForm = this.formBuilder.group({
      email: [this.email, [
        Validators.required,
        Validators.pattern(NIGHTWEB_APP_CONSTANT.emailPattern)
      ]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(NIGHTWEB_APP_CONSTANT.passwordMinLength),
        Validators.pattern(NIGHTWEB_APP_CONSTANT.passwordPattern)
      ]],
      confirmNewPassword: ['', [
        Validators.required,
        Validators.minLength(NIGHTWEB_APP_CONSTANT.passwordMinLength),
        Validators.pattern(NIGHTWEB_APP_CONSTANT.passwordPattern)
      ]],
      token: [this.verifyToken, Validators.required]
    },
      { validator: PasswordValidation.MatchPassword })
  }

  activate() {
    if (this.activateForm.valid) {
      this.loading = true;
      this.userService.setEndUserPassword(this.activateForm.value)
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: () => {
            this.toasterService.show('Success', MESSAGE_CONSTANTS.RESET_PASSWORD);
            this.authService.logout();
          },
          error: () => { }
        })
    }
  }

}
